/**
 * Partage : feuille NATIVE sur iOS / Android (plugin `@capacitor/share`, la
 * WebView Android n'a pas `navigator.share`), Web Share API dans un navigateur
 * qui la propose, sinon copie du lien dans le presse-papiers.
 */

import { Capacitor } from '@capacitor/core';

export interface ShareData {
  title: string;
  text: string;
  url: string;
}

export type ShareResult = 'shared' | 'copied' | 'failed';

/** Annulation par l'utilisateur (natif : « Share canceled » ; web : AbortError). */
export function isShareCancel(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  return e.name === 'AbortError' || /cancel/i.test(e.message);
}

export async function shareContent(data: ShareData): Promise<ShareResult> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({
        title: data.title,
        text: data.text,
        url: data.url,
        dialogTitle: data.title,
      });
      return 'shared';
    } catch (e) {
      if (isShareCancel(e)) return 'shared';
      // Plugin absent d'une ancienne build native : on tente le web.
    }
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share(data);
      return 'shared';
    }
  } catch (e) {
    if (isShareCancel(e)) return 'shared';
  }
  try {
    await navigator.clipboard.writeText(`${data.text} ${data.url}`.trim());
    return 'copied';
  } catch {
    return 'failed';
  }
}
